import {
  SYSTEM_KEY__OBJECTIVE_ROOT_AUTHORITY,
  collectAllPages,
} from "@examspark/common";
import {
  IDSK,
  Maybe,
  ModelObjectiveConnection,
  Objective,
  ObjectiveEditorDocument,
  ObjectiveScope,
  Resource,
  SearchableObjectiveFilterInput,
  SearchableObjectiveSortInput,
} from "@examspark/types";

import { GraphQLAPI, queries } from "../..";
import { GraphQLResult } from "../../..";
import { updateResource } from "../resource";

export const _listObjectives = /* GraphQL */ `
  query ListObjectives(
    $id: ID
    $sk: ModelStringKeyConditionInput
    $filter: ModelObjectiveFilterInput
    $limit: Int
    $nextToken: String
    $sortDirection: ModelSortDirection
  ) {
    listObjectives(
      id: $id
      sk: $sk
      filter: $filter
      limit: $limit
      nextToken: $nextToken
      sortDirection: $sortDirection
    ) {
      items {
        id
        sk
        authorityId
        content
        guid
        idAuthority
        idPublisher
        isObsolete
        name
        numEnhanced
        numRaw
        objectiveTree
        parentId
        scope
        sequence
        type
        workflowStatus
      }
    }
  }
`;

export const listObjectivesByScope = async (scope: string) => {
  const result = await GraphQLAPI.client.query<{
    listObjectivesByScopeSequence: any;
  }>({
    query: /* GraphQL */ `
      query ListObjectivesByScopeSequence(
        $scope: String
        $sequence: ModelIntKeyConditionInput
        $sortDirection: ModelSortDirection
        $filter: ModelObjectiveFilterInput
        $limit: Int
        $nextToken: String
      ) {
        listObjectivesByScopeSequence(
          scope: $scope
          sequence: $sequence
          sortDirection: $sortDirection
          filter: $filter
          limit: $limit
          nextToken: $nextToken
        ) {
          items {
            id
            sk
            scope
            abbreviation
            outlineNumber
            content
            sequence
            notes
          }
        }
      }
    `,
    variables: {
      limit: 200,
      scope,
    },
  });

  return result.data?.listObjectivesByScopeSequence;
};

export const getObjectiveQuery = async (variables: IDSK) => {
  const result = await GraphQLAPI.client.query<{
    getObjective: Objective;
  }>({
    query: /* GraphQL */ `
      query GetObjective($id: ID!, $sk: String!) {
        getObjective(id: $id, sk: $sk) {
          id
          sk
          guid
          type
          name
          content
          numRaw
          numEnhanced
          outlineNumber
          abbreviation
          sequence
          idAuthority
          idPublisher
          parentId
          scope
          isAlignable
          objectiveTree
          workflowStatus
          versionCurrent
          versionNext
          versionLabel
          documentId
          authorityId
          publicationId
          createdAt
          updatedAt
        }
      }
    `,
    variables,
  });

  return result.data?.getObjective;
};

export const getObjective = async (id: string) => {
  const result = await GraphQLAPI.client.query<{
    getObjective: Objective;
  }>({
    query: /* GraphQL */ `
      query GetObjective($id: ID!, $sk: String!) {
        getObjective(id: $id, sk: $sk) {
          id
          sk
          abbreviation
          annotations {
            label
            content
          }
          authorityId
          content
          createdAt
          documentId
          guid
          idAuthority
          idPublisher
          isAlignable
          name
          notes
          numEnhanced
          numRaw
          objectiveTree
          outlineNumber
          parentId
          publicationId
          scope
          sequence
          tags {
            key
            type
            value
          }
          type
          updatedAt
          versionCurrent
          versionLabel
          versionNext
          workflowStatus
        }
      }
    `,
    variables: {
      id,
      sk: "standard",
    },
  });

  return result.data?.getObjective;
};

export const createObjectiveMutation = async (variables: any) => {
  const rsp = await GraphQLAPI.client.query<{
    createObjective: Objective;
  }>({
    query: /* GraphQL */ `
      mutation CreateObjective($input: CreateObjectiveInput!) {
        createObjective(input: $input) {
          id
          sk
          guid
          type
          name
          content
          numRaw
          numEnhanced
          sequence
          idAuthority
          idPublisher
          objectiveTree
        }
      }
    `,
    variables,
  });

  return rsp;
};

export const updateObjective = async (variables: any) => {
  const rsp = await GraphQLAPI.client.query<{
    updateObjective: Objective;
  }>({
    query: /* GraphQL */ `
      mutation UpdateObjective($input: UpdateObjectiveInput!) {
        updateObjective(input: $input) {
          id
          sk
          guid
          type
          name
          content
          numRaw
          numEnhanced
          sequence
          idAuthority
          idPublisher
          objectiveTree
          updatedAt
        }
      }
    `,
    variables,
  });

  return rsp;
};

export const updateObjectiveCustomData = async (
  id: string,
  sk: string,
  data: Pick<Objective, "notes" | "annotations" | "tags">,
) =>
  updateObjective({
    input: {
      id: id,
      sk: sk,
      notes: data.notes,
      annotations:
        data.annotations?.map((a) => {
          const { __typename, ...rest } = a;
          return rest;
        }) ?? null,
      tags: data.tags,
    },
  });

export const updateDocument = async (
  id: string,
  sk: string,
  document: ObjectiveEditorDocument,
) => {
  updateObjective({
    input: {
      id: id,
      sk: sk,
      content: document.name,
    },
  });
};

export const deleteObjective = async (variables: any) => {
  const rsp = await GraphQLAPI.client.query<{
    deleteObjective: Objective;
  }>({
    query: /* GraphQL */ `
      mutation DeleteObjective($input: DeleteObjectiveInput!) {
        deleteObjective(input: $input) {
          id
          sk
          guid
          type
          name
          content
          numRaw
          numEnhanced
          sequence
          idAuthority
          idPublisher
        }
      }
    `,
    variables,
  });

  return rsp;
};

export const listObjectives = async (variables: any) => {
  const rsp = await GraphQLAPI.client.query<{
    listObjectives: {
      items: Objective[];
    };
  }>({
    query: _listObjectives,
    variables,
  });

  return rsp;
};

export const listAuthorities = async () => {
  const rsp = await listObjectives({
    id: SYSTEM_KEY__OBJECTIVE_ROOT_AUTHORITY,
  });

  return rsp.data?.listObjectives;
};

export const listDocumentsForWorkspace = async (workspaceId: string) => {
  const rsp = await listObjectives({
    id: workspaceId,
    sk: {
      beginsWith: "document#",
    },
  });

  return rsp.data?.listObjectives;
};

export const listDocumentsForAuthority = async (authorityId: string) => {
  const rsp = await listObjectives({
    id: authorityId,
    sk: {
      beginsWith: "document#",
    },
  });

  return rsp.data?.listObjectives;
};

export const listPublicationsForAuthority = async (authorityId: string) => {
  const rsp = await listObjectives({
    id: authorityId,
    sk: {
      beginsWith: ["publication", authorityId].join("#"),
    },
  });

  return rsp.data?.listObjectives;
};

export const listDocumentsForPublication = async (
  authorityId: string,
  publicationId: string,
) => {
  const rsp = await listObjectives({
    id: authorityId,
    sk: {
      beginsWith: ["document", authorityId, publicationId].join("#"),
    },
  });

  return rsp.data?.listObjectives;
};

export const listSectionsForDocument = async (
  authorityId: string,
  publicationId: string,
  documentId: string,
) => {
  const rsp = await listObjectives({
    id: authorityId,
    sk: {
      beginsWith:
        ["section", authorityId, publicationId, documentId].join("#") + "#",
    },
  });

  return rsp.data?.listObjectives;
};

export const listStandardsForScope = async (scope: ObjectiveScope) => {
  const rsp = await listObjectives({
    limit: 500,
    id: scope.authority,
    sk: {
      beginsWith: [
        "standard",
        scope.publication,
        scope.document,
        scope.section,
        "",
      ].join("#"),
    },
  });
  return rsp.data?.listObjectives;
};

export const deleteDocument = async (id: string, sk: string) => {
  return deleteObjective({
    input: {
      id,
      sk,
    },
  });
};

export const deleteSection = async (id: string, sk: string) => {
  return deleteObjective({
    input: {
      id,
      sk,
    },
  });
};

export const deleteSectionsForDocument = async (
  authorityId: string,
  publicationId: string,
  documentId: string,
) => {
  // list all sections to know which ones to delete
  const sections = await listSectionsForDocument(
    authorityId,
    publicationId,
    documentId,
  );

  const promises = sections?.items.map((section: Objective) =>
    deleteSection(section.id, section.sk),
  );

  await Promise.all(promises ?? []);
};

export const createAuthority = async (id: string, name: string) => {
  const promises: Promise<any>[] = [];

  promises.push(
    createObjectiveMutation({
      input: {
        id: SYSTEM_KEY__OBJECTIVE_ROOT_AUTHORITY,
        sk: id,
        type: "AUTHORITY",
        content: name,
      },
    }),
  );

  promises.push(
    createObjectiveMutation({
      input: {
        id: id,
        sk: "authority",
        type: "AUTHORITY",
        content: name,
      },
    }),
  );

  await Promise.all(promises);
  return promises;
};

export const createPublication = async (
  authorityId: string,
  publicationId: string,
  publicationName: string,
) => {
  const promises: Promise<any>[] = [];

  promises.push(
    createObjectiveMutation({
      input: {
        id: authorityId,
        sk: ["publication", authorityId, publicationId.toLowerCase()].join("#"),
        content: publicationName,
        type: "PUBLICATION",
      },
    }),
  );

  await Promise.all(promises);
  return promises;
};

export const createDocument = async (
  authorityId: string,
  publicationId: string,
  document: ObjectiveEditorDocument,
) => {
  const documentId = [document.abbreviation, document.adoptionYear]
    .join(":")
    .toLowerCase();

  return createObjectiveMutation({
    input: {
      id: authorityId,
      sk: ["document", authorityId, publicationId, documentId]
        .join("#")
        .toLowerCase(),
      type: "DOCUMENT",
      workflowStatus: "DRAFT",
      content: document.name,
    },
  });
};

export const createSection = async (
  authorityId: string,
  publicationId: string,
  documentId: string,
  sectionId: string,
  data: Partial<Objective>,
) => {
  return createObjectiveMutation({
    input: Object.assign(
      {
        id: authorityId,
        sk: ["section", authorityId, publicationId, documentId, sectionId]
          .join("#")
          .toLowerCase(),
        type: "SECTION",
      },
      data,
    ),
  });
};

export const getAuthority = async (authorityId: string) => {
  return getObjectiveQuery({
    id: authorityId,
    sk: "authority",
  });
};

export const getPublication = async (
  authorityId: string,
  publicationId: string,
) => {
  return getObjectiveQuery({
    id: authorityId,
    sk: ["publication", authorityId, publicationId].join("#"),
  });
};

export const getDocument = async (
  authorityId: string,
  publicationId: string,
  documentId: string,
) => {
  return getObjectiveQuery({
    id: authorityId,
    sk: ["document", authorityId, publicationId, documentId].join("#"),
  });
};

export const invokeObjective = /* GraphQL */ `
  mutation InvokeObjective($input: InvokeObjectiveInput) {
    invokeObjective(input: $input)
  }
`;

export const publishDocument = async (id: string, sk: string) => {
  // return invokeObjectiveMutation({
  //   variables: {
  //     input: {
  //       workspaceId: id,
  //       action: "objective.document.publish",
  //       payload: JSON.stringify({
  //         id,
  //         sk,
  //       }),
  //     },
  //   },
  // });
};

export const addObjectives = async (
  resource: Resource,
  objectivesToAdd: string[],
) => {
  const result = await GraphQLAPI.client.query<{ getResource: Resource }>({
    query: /* GraphQL */ `
      query GetObjectives($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          alignmentIds
        }
      }
    `,
    variables: {
      id: resource.id,
      sk: resource.sk,
    },
  });

  const objectiveIds = new Set(result.data?.getResource.alignmentIds || []);
  for (const obj of objectivesToAdd) {
    objectiveIds.add(obj);
  }

  const updateResult = await updateResource({
    id: resource.id,
    sk: resource.sk,
    alignmentIds: Array.from(objectiveIds),
  });

  return updateResult.data?.updateResource;
};

export const removeObjectives = async (
  resource: Resource,
  objectivesToDelete: string[],
) => {
  const result = await GraphQLAPI.client.query<{ getResource: Resource }>({
    query: /* GraphQL */ `
      query GetObjectives($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          alignmentIds
        }
      }
    `,
    variables: {
      id: resource.id,
      sk: resource.sk,
    },
  });

  const objectiveIds = new Set(result.data?.getResource.alignmentIds || []);
  for (const obj of objectivesToDelete) {
    objectiveIds.delete(obj);
  }

  const updateResult = await updateResource({
    id: resource.id,
    sk: resource.sk,
    alignmentIds: Array.from(objectiveIds),
  });

  return updateResult.data?.updateResource;
};

export const getObjectiveDocument = async (
  authorityId: string,
  documentId: string,
) => {
  const result = await GraphQLAPI.client.query<{
    getObjective: Maybe<Objective>;
  }>({
    query: /* GraphQL */ `
      query GetObjectiveDocument($id: ID!, $sk: String!) {
        getObjective(id: $id, sk: $sk) {
          id
          sk
          guid
          type
          name
          content
          numRaw
          numEnhanced
          sequence
          authorityId
          idAuthority
          idPublisher
          parentId
          scope
          objectiveTree
          workflowStatus
          isObsolete
        }
      }
    `,
    variables: {
      id: authorityId,
      sk: ["document", documentId].join("#"),
    },
  });

  return result.data?.getObjective;
};

export const listObjectiveSections = async (
  authorityId: string,
  documentId: string,
) => {
  const result = await GraphQLAPI.client.query<{
    listObjectives: ModelObjectiveConnection;
  }>({
    query: queries.objective.listObjectives,
    variables: {
      id: authorityId,
      sk: {
        beginsWith: documentId,
      },
    },
  });

  return result.data?.listObjectives;
};

const _searchObjectives = async (scopes: string[], fields: string) => {
  const results = await collectAllPages(async (nextToken) => {
    const result = (await GraphQLAPI.query({
      query: /* GraphQL */ `
        query SearchObjectives(
          $filter: SearchableObjectiveFilterInput
          $sort: SearchableObjectiveSortInput
          $limit: Int
          $nextToken: String
          $from: Int
        ) {
          searchObjectives(
            filter: $filter
            sort: $sort
            limit: $limit
            nextToken: $nextToken
            from: $from
          ) {
            items {
              ${fields}
            }
            nextToken
            total
          }
        }
      `,
      variables: {
        sort: {
          field: "sequence",
          direction: "asc",
        } as SearchableObjectiveSortInput,
        filter: {
          or: scopes.map((scope) => ({
            scope: {
              wildcard: `${scope}*`,
            },
          })),
        } as SearchableObjectiveFilterInput,
        nextToken,
      },
    })) as GraphQLResult<{
      searchObjectives: {
        items: Objective[] | undefined;
        nextToken: string | undefined;
      };
    }>;

    return result.data?.searchObjectives;
  });

  return results.flatMap((o) => o.items ?? []);
};

// normal fields + bonfire-specific objective data (annotations, notes)
export const searchBonfireObjectives = async (scopes: string[]) => {
  return _searchObjectives(
    scopes,
    `
    id
    authorityId
    scope
    abbreviation
    outlineNumber
    notes
    annotations {
      label
      content
    }
    content
    sequence
    tags {
      key
      value
    }
  `,
  );
};

export const searchObjectives = async (scopes: string[]) => {
  return _searchObjectives(
    scopes,
    `
    id
    authorityId
    scope
    abbreviation
    outlineNumber
    content
    sequence
    tags {
      key
      value
    }
  `,
  );
};
